import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MessagingService } from 'src/app/core/messaging/messaging.service';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ButtonType } from 'src/app/shared/common/components/buttons/button';
import { NotificationType } from 'src/app/shared/common/components/gsp-notification/gsp-notification.component';
import { FeaturesStreamsService } from 'src/app/shared/common/current-features/features-streams.service';
import { TaskFeaturesStreamsService } from 'src/app/shared/common/current-features/task-features-streams.service';
import { LayersStore } from 'src/app/shared/common/current-layers/layers-store.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { TasksStore } from 'src/app/shared/common/current-tasks/tasks-store.service';
import { TasksStreamsService } from 'src/app/shared/common/current-tasks/tasks-streams.service';
import { TemplatesStoreService } from 'src/app/shared/common/current-templates/templates-store.service';
import { CurrentUserStreamService } from 'src/app/shared/common/current-user/current-user-stream.service';
import { Task, TaskCreationMode, TaskPanelTabs, TaskStatus } from 'src/app/shared/common/task/task';
import { CloneUtils } from 'src/app/shared/common/utility/clone-utils';
import { GeometryTypes } from 'src/app/shared/common/utility/geometry-utils';
import { CachedFeatureService } from 'src/app/shared/map-data-services/feature/cached-feature.service';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { FeatureService } from 'src/app/shared/map-data-services/feature/feature.service';
import { Layer } from 'src/app/shared/map-data-services/layer/layer';
import { PermissionType } from 'src/app/shared/map-data-services/mapWorkspace/permission';
import { Project } from 'src/app/shared/map-data-services/project/project';
import { Template } from 'src/app/shared/template-services/template';
import { Actor } from 'src/app/shared/user/actor';
import { User, UserRole } from 'src/app/shared/user/user';

// class representing feature on the task detail panel
class TaskPanelLayer {
    templateSeriesId: string;
    layer: Layer;
    layerColor: string;
    isLayerRemoved: boolean;
    featureIds: string[];
}

@Component({
    selector: 'task-detail-panel',
    templateUrl: './task-detail-panel.component.html'
})
export class TaskDetailPanelComponent implements OnInit, OnDestroy {
    @Input()
    taskId: string;

    @Output()
    setTaskPanelTab = new EventEmitter<TaskPanelTabs>();

    // expose enums to template
    public ButtonType = ButtonType;
    public NotificationType = NotificationType;
    public PermissionType = PermissionType;
    public TaskCreationMode = TaskCreationMode;
    public TaskStatus = TaskStatus;

    private readonly destroyed = new Subject<void>();

    public currentProject: Project;
    public currentUserId: string;
    public task: Task = null;
    public numberOfNonTemplatedFeaturesExcluded = 0;
    public editMode = false;
    public deleteTaskConfirmation = false;
    public closeOrReopenTaskConfirmation = false;
    public disableEdit = true;
    public taskTitleRequired = false;
    public originalTask: Task = null;
    public taskCreationMode: TaskCreationMode;
    public loading = true;
    public taskFeaturesLoading = false;
    public allPotentialAssignees: User[] = [];
    public layerKeyToMapWorkspaceLayer: { [key: string]: Layer };
    public templatesBySeriesId: { [templateId: string]: Template } = {};
    public templateSeriesIdToTaskPanelLayer: { [key: string]: TaskPanelLayer } = {};
    public taskPanelLayers: TaskPanelLayer[] = [];
    public isRemoveFeatureInProgress = false;
    public modifyPermission = false;
    public currentProjectAssignees: Actor[] = [];
    public taskFormLayers: Layer[] = [];
    private taskFeatures: Feature[] = [];
    public showAssignedUsersPanel = false;
    public maxTaskFeatureCount = 5000;

    constructor(
        private projectStream: ProjectStreamService,
        private currentUserStream: CurrentUserStreamService,
        private translate: TranslationService,
        private tasksStore: TasksStore,
        private taskFeaturesStreams: TaskFeaturesStreamsService,
        private featuresStreams: FeaturesStreamsService,
        private cachedFeatureService: CachedFeatureService,
        private layersStore: LayersStore,
        private templatesStore: TemplatesStoreService,
        private messaging: MessagingService,
        private featureService: FeatureService,
        private tasksStreams: TasksStreamsService
    ) {}

    async ngOnInit(): Promise<void> {
        this.currentUserId = this.currentUserStream.currentUser.id;
        this.currentProjectAssignees = this.tasksStore.getAllAssignees();
        this.layerKeyToMapWorkspaceLayer = this.layersStore.getLayerKeyToLayerMap();

        this.projectStream.currentProjectStream
            .pipe(takeUntil(this.destroyed))
            .subscribe(project => (this.currentProject = project));
        if (this.taskId) {
            this.resetTaskPanelOnCancelEdit();
            this.task = await this.tasksStore.getTaskById(this.taskId);
        } else {
            this.task = await this.tasksStore.createNewTask();
            this.taskCreationMode = TaskCreationMode.NEW;
        }

        if (this.task) {
            await this.setModifyPermission();
            this.disableEdit = this.task.status === TaskStatus.COMPLETE;
            if (this.taskCreationMode === TaskCreationMode.NEW) {
                this.featuresStreams.selectedFeaturesStream.pipe(takeUntil(this.destroyed)).subscribe(features => {
                    this.task.features = this.tasksStore.getTaskFeaturesFromSelectedFeatures(features);
                    this.loadLayersForTaskFeatures();
                });
            } else {
                this.loadLayersForTaskFeatures();
            }
            this.subscribeToPendingLoadingTaskStream();
            this.originalTask = CloneUtils.cloneDeep(this.task);
        }
        this.loading = false;
    }

    private resetTaskPanelOnCancelEdit() {
        this.tasksStreams.selectedTasksStream
            .pipe(
                takeUntil(this.destroyed),
                filter(() => this.taskCreationMode === TaskCreationMode.EDIT)
            )
            .subscribe(async () => {
                this.taskCreationMode = null;
                this.loading = true;
                await this.tasksStore.getTaskById(this.taskId).then(task => {
                    this.task = task;
                    this.loadLayersForTaskFeatures();
                    this.loading = false;
                });
            });
    }

    closeOrReopenTaskAction = () => this.closeOrReopenTask().then(() => this.back());

    public async closeOrReopenTask(): Promise<void> {
        if (!this.modifyPermission) {
            this.messaging.showWarning(this.translate.instant('PERMISSION_DENIED'));
            return;
        }

        const newStatus = this.task.isClosed
            ? this.task.percentageComplete > 0
                ? TaskStatus.IN_PROGRESS
                : TaskStatus.NEW
            : TaskStatus.COMPLETE;

        const newTabStatus = newStatus === TaskStatus.COMPLETE ? TaskPanelTabs.CLOSED : TaskPanelTabs.ACTIVE;

        this.loading = true;

        if (newStatus === TaskStatus.COMPLETE) {
            await this.tasksStore.closeTask(this.task);
        } else {
            await this.tasksStore.reopenTask(this.task);
        }
        this.task.isClosed = newStatus === TaskStatus.COMPLETE;
        this.task.visible = newStatus !== TaskStatus.COMPLETE;

        const visibleTasks = this.tasksStreams.visibleTasksStream.getValue();
        this.tasksStreams.setVisibleTasks(visibleTasks.filter(x => x.visible));

        if (this.task.isClosed) {
            this.taskFeaturesStreams.getFeaturesByTask(this.task).then(data => {
                this.task.taskFeatures = data;
                this.tasksStreams.deferHideTasksStream.next([this.task]);
            });
        }

        // Refresh tabs
        this.tasksStore.loadCurrentWorkspaceTasksBySearch(TaskPanelTabs.CLOSED, '');
        this.tasksStore.loadCurrentWorkspaceTasksBySearch(TaskPanelTabs.ACTIVE, '');

        this.setTaskPanelTab.emit(newTabStatus);
    }

    private subscribeToPendingLoadingTaskStream(): void {
        // update task.taskFeatures on task feature visibility changes
        this.tasksStreams.pendingLoadingTaskStream.pipe(takeUntil(this.destroyed)).subscribe(pendingTaskObj => {
            if (pendingTaskObj && pendingTaskObj.loading) {
                if (this.task.id === pendingTaskObj.task.id) {
                    this.task = pendingTaskObj.task;
                }
            }
        });
    }

    public get exceedsLimit(): boolean {
        return this.task && this.task.features && this.task.features.length > this.maxTaskFeatureCount;
    }

    // ensure all layers, including those no longer in the workspace, are loaded
    private loadLayersForTaskFeatures(): Promise<void> {
        let layerKeyToLayerMap = this.layersStore.getLayerKeyToLayerMap(); // layers in current workspace
        let promises: Promise<void>[] = [];
        this.templateSeriesIdToTaskPanelLayer = {};
        this.taskFeatures = [];
        this.taskFeaturesLoading = true;
        this.task.features.forEach(taskFeature => {
            if (!this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId]) {
                this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId] = {
                    templateSeriesId: taskFeature.templateSeriesId,
                    layer: null,
                    layerColor: '#000000',
                    isLayerRemoved: false,
                    featureIds: []
                };

                let loadTemplatePromise = this.templatesStore
                    .getTemplateBySeriesId(taskFeature.templateSeriesId)
                    .then(template => {
                        this.templatesBySeriesId[taskFeature.templateSeriesId] = template;
                    });
                promises.push(loadTemplatePromise);
            }

            // initially get the feature (from cache or API)
            let feature = this.cachedFeatureService.getFeature(taskFeature.featureId);
            let getFeaturePromise =
                feature && !feature.deleted
                    ? Promise.resolve(feature)
                    : this.featureService.getFeatureById(this.currentProject.id, taskFeature.featureId);

            let loadLayerPromise = getFeaturePromise.then(feature2 => {
                this.taskFeatures.push(feature2);
                let layerKey = feature2.getLayerKey();
                let isLayerRemoved = !Boolean(layerKeyToLayerMap[layerKey]);
                if (layerKey) {
                    let layerId = layerKey; // for templated layers, this is the layerId
                    // gets non-ws layers too
                    return this.layersStore.getMapWorkspaceLayerByIdPromise(layerId).then(layer => {
                        let taskPanelLayer = this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId];
                        taskPanelLayer.layer = layer;
                        taskPanelLayer.layerColor = layer.getLayerColor();
                        taskPanelLayer.isLayerRemoved = isLayerRemoved;
                    });
                } else {
                    Promise.resolve();
                }
            });
            promises.push(loadLayerPromise);

            this.templateSeriesIdToTaskPanelLayer[taskFeature.templateSeriesId].featureIds.push(taskFeature.featureId);
        });
        this.task.taskFeatures = this.taskFeatures;

        return Promise.all(promises).then(() => {
            this.taskPanelLayers = Object.values(this.templateSeriesIdToTaskPanelLayer);
            this.taskFeaturesLoading = false;
        });
    }

    public getLayerName(layer: Layer): string {
        return layer ? layer.layerName : this.translate.instant('TC.Common.NoneParenthesis');
    }

    public getTemplateGeometryType(templateSeriesId: string): string {
        let template = this.templatesBySeriesId[templateSeriesId];
        return template ? template.geometryType : GeometryTypes.NONE;
    }

    public getTaskFormsByLayer(layer: Layer): Feature[] {
        return this.task.taskFeatures.filter(feature => feature.id !== null && feature.layerId === layer.id);
    }

    public back() {
        this.tasksStreams.setSelectedTasks([]);
        this.tasksStreams.disableMapviewerStream.next(true);
        this.taskCreationMode = null;
    }

    private async setModifyPermission(): Promise<void> {
        const user = await this.projectStream.getUserWithRolesForCurrentProject(this.currentUserId);
        this.modifyPermission = user.role === UserRole.ADMIN || this.task.createdBy === this.currentUserId;
    }

    public editTask() {
        if (!this.modifyPermission) {
            this.messaging.showWarning(this.translate.instant('PERMISSION_DENIED'));
            return;
        }

        this.taskCreationMode = TaskCreationMode.EDIT;

        if (this.tasksStreams.disableMapviewerStream.getValue()) {
            this.tasksStreams.disableMapviewerStream.next(true);
        }
    }

    public deleteAction = () => this.deleteTask().then(() => this.back());

    private deleteTask(): Promise<void> {
        if (!this.modifyPermission) {
            this.messaging.showWarning(this.translate.instant('PERMISSION_DENIED'));
            return Promise.resolve();
        }

        return this.tasksStore.deleteTask(this.task).then(
            () => {
                this.messaging.showSuccess(this.translate.instant('TC.TodosTask.Message.Deleted'));
                this.originalTask = CloneUtils.cloneDeep(this.task);
                // hide the selection of task features in the mapViewer after deletion if visible
                if (this.task.visible) {
                    this.tasksStreams.deferHideTasksStream.next([this.task]);
                }
            },
            () => {
                this.messaging.showError(this.translate.instant('TC.Common.ErrorWhileDeleting'));
            }
        );
    }

    public toggleAssignedUsersPanel() {
        this.showAssignedUsersPanel = !this.showAssignedUsersPanel;
    }

    public selectAllForms() {
        let taskFeatureIds = this.task.features.map(feature => feature.featureId);
        this.featuresStreams.replaceOnSelectedFeatures(taskFeatureIds);
        this.tasksStreams.disableMapviewerStream.next(false);
    }

    ngOnDestroy(): void {
        this.destroyed.next(null);
    }
}
